import type { ActionButtonsControllerValue } from '@/components/action-buttons/action-buttons-controller.types';

type ActionButtonsButtonRowBindings = ActionButtonsControllerValue['buttonRowBindings'];
type ActionButtonsModalsBindings = ActionButtonsControllerValue['modalsBindings'];

export type ActionButtonsControllerViewModelBindings = Pick<
    ActionButtonsButtonRowBindings,
    | 'isReviewsTable'
    | 'isGroupTable'
    | 'isBrandsTable'
    | 'selectedItemsCount'
    | 'selectedItemsCountBadgeClassName'
    | 'reviewsSelectionPriority'
    | 'groupSelectionPriority'
    | 'mergePriority'
    | 'isReplyDisabled'
    | 'replyDisabledReason'
    | 'isReplyQuotaExhausted'
    | 'isAccountLimitZero'
    | 'reviewsExportCountBadgeClassName'
    | 'groupsExportCount'
    | 'groupsExportCountBadgeClassName'
    | 'isAiAnalysisForReviewsDisabled'
    | 'isAiAnalysisForGroupDisabled'
    | 'aiAnalysisDisabledReason'
    | 'isAiAnalysisQuotaExhausted'
    | 'isCombinedAnalysisForGroupDisabled'
    | 'isMergeActiveGroupDisabled'
>;

export type ActionButtonsControllerUiBindings = Pick<
    ActionButtonsButtonRowBindings,
    | 'isAllPickLoading'
    | 'isGroupsMergeLoading'
    | 'isReportModalOpen'
    | 'setIsReportModalOpen'
    | 'reportSummaryRefreshToken'
    | 'isAiModalOpen'
    | 'setIsAiModalOpen'
    | 'isAnswerModalOpen'
    | 'setIsAnswerModalOpen'
> &
    Pick<ActionButtonsModalsBindings, 'pickedReviewsAnswer' | 'setPickedReviewsAnswer'>;

export type ActionButtonsControllerActionHandlers = Pick<
    ActionButtonsButtonRowBindings,
    | 'onAllPicked'
    | 'onExportCsv'
    | 'onExportProductsCsv'
    | 'onCancelExport'
    | 'onRunAiAnalysisForSelectedReviews'
    | 'onRunAiAnalysisForSelectedProducts'
    | 'onCancelAiAnalysis'
    | 'onOpenCombinedAnalysis'
    | 'onGroupPickedProducts'
    | 'onGroupPickedBrands'
    | 'onCancelActiveAnswerJobs'
>;

export type ActionButtonsControllerCombinedAnalysisBindings = Pick<
    ActionButtonsModalsBindings,
    'isCombinedAnalysisModalOpen' | 'closeCombinedAnalysis' | 'openedCombinedAnalysis' | 'combinedAnalysisError'
> &
    Pick<ActionButtonsButtonRowBindings, 'isCombinedAnalysisLoading'>;

export type ActionButtonsControllerGroupMergeBindings = Pick<
    ActionButtonsModalsBindings,
    | 'isGroupMergeModalOpen'
    | 'closeGroupMergeModal'
    | 'groupMergeNameDraft'
    | 'setGroupMergeNameDraft'
    | 'submitGroupMergeForm'
    | 'isGroupMergeSubmitDisabled'
    | 'groupMergeEntityLabel'
>;

export type ActionButtonsControllerProgressBindings = Pick<
    ActionButtonsButtonRowBindings,
    | 'isExportLoading'
    | 'isSelectedItemsCountSyncing'
    | 'isAiAnalysisLoading'
    | 'isAnswerJobsCancelLoading'
    | 'hasActiveAnswerJobs'
>;

export type ActionButtonsControllerPickedCounts = Pick<ActionButtonsButtonRowBindings, 'pickedReviewsCount'> &
    Pick<ActionButtonsModalsBindings, 'pickedProductsCount' | 'pickedBrandGroupsCount'>;

export type BuildActionButtonsControllerBindingsArgs = {
    activeTable: ActionButtonsButtonRowBindings['activeTable'];
    viewModel: ActionButtonsControllerViewModelBindings;
    uiState: ActionButtonsControllerUiBindings;
    actionHandlers: ActionButtonsControllerActionHandlers;
    combinedAnalysis: ActionButtonsControllerCombinedAnalysisBindings;
    groupMerge: ActionButtonsControllerGroupMergeBindings;
    progress: ActionButtonsControllerProgressBindings;
    pickedCounts: ActionButtonsControllerPickedCounts;
};

export const buildActionButtonsControllerBindings = ({
    activeTable,
    viewModel,
    uiState,
    actionHandlers,
    combinedAnalysis,
    groupMerge,
    progress,
    pickedCounts,
}: BuildActionButtonsControllerBindingsArgs): ActionButtonsControllerValue => {
    const buttonRowBindings: ActionButtonsButtonRowBindings = {
        activeTable,
        isReviewsTable: viewModel.isReviewsTable,
        isGroupTable: viewModel.isGroupTable,
        isBrandsTable: viewModel.isBrandsTable,
        selectedItemsCount: viewModel.selectedItemsCount,
        selectedItemsCountBadgeClassName: viewModel.selectedItemsCountBadgeClassName,
        isAllPickLoading: uiState.isAllPickLoading,
        onAllPicked: actionHandlers.onAllPicked,
        reviewsSelectionPriority: viewModel.reviewsSelectionPriority,
        groupSelectionPriority: viewModel.groupSelectionPriority,
        mergePriority: viewModel.mergePriority,
        isReplyDisabled: viewModel.isReplyDisabled,
        replyDisabledReason: viewModel.replyDisabledReason,
        isReplyQuotaExhausted: viewModel.isReplyQuotaExhausted,
        isAccountLimitZero: viewModel.isAccountLimitZero,
        showAccountTopUpButton: viewModel.isAccountLimitZero,
        isExportLoading: progress.isExportLoading,
        isSelectedItemsCountSyncing: progress.isSelectedItemsCountSyncing,
        pickedReviewsCount: pickedCounts.pickedReviewsCount,
        reviewsExportCountBadgeClassName: viewModel.reviewsExportCountBadgeClassName,
        groupsExportCount: viewModel.groupsExportCount,
        groupsExportCountBadgeClassName: viewModel.groupsExportCountBadgeClassName,
        onExportCsv: actionHandlers.onExportCsv,
        onExportProductsCsv: actionHandlers.onExportProductsCsv,
        onCancelExport: actionHandlers.onCancelExport,
        isAiAnalysisLoading: progress.isAiAnalysisLoading,
        isAiAnalysisForReviewsDisabled: viewModel.isAiAnalysisForReviewsDisabled,
        aiAnalysisDisabledReason: viewModel.aiAnalysisDisabledReason,
        isAiAnalysisQuotaExhausted: viewModel.isAiAnalysisQuotaExhausted,
        onRunAiAnalysisForSelectedReviews: actionHandlers.onRunAiAnalysisForSelectedReviews,
        onRunAiAnalysisForSelectedProducts: actionHandlers.onRunAiAnalysisForSelectedProducts,
        onCancelAiAnalysis: actionHandlers.onCancelAiAnalysis,
        isAiAnalysisForGroupDisabled: viewModel.isAiAnalysisForGroupDisabled,
        onOpenCombinedAnalysis: actionHandlers.onOpenCombinedAnalysis,
        isCombinedAnalysisLoading: combinedAnalysis.isCombinedAnalysisLoading,
        isCombinedAnalysisForGroupDisabled: viewModel.isCombinedAnalysisForGroupDisabled,
        onGroupPickedProducts: actionHandlers.onGroupPickedProducts,
        onGroupPickedBrands: actionHandlers.onGroupPickedBrands,
        isGroupsMergeLoading: uiState.isGroupsMergeLoading,
        isMergeActiveGroupDisabled: viewModel.isMergeActiveGroupDisabled,
        isReportModalOpen: uiState.isReportModalOpen,
        setIsReportModalOpen: uiState.setIsReportModalOpen,
        reportSummaryRefreshToken: uiState.reportSummaryRefreshToken,
        isAiModalOpen: uiState.isAiModalOpen,
        setIsAiModalOpen: uiState.setIsAiModalOpen,
        isAnswerModalOpen: uiState.isAnswerModalOpen,
        setIsAnswerModalOpen: uiState.setIsAnswerModalOpen,
        hasActiveAnswerJobs: progress.hasActiveAnswerJobs,
        onCancelActiveAnswerJobs: actionHandlers.onCancelActiveAnswerJobs,
        isAnswerJobsCancelLoading: progress.isAnswerJobsCancelLoading,
    };

    const modalsBindings: ActionButtonsModalsBindings = {
        activeTable,
        isGroupTable: viewModel.isGroupTable,
        isBrandsTable: viewModel.isBrandsTable,
        isReportModalOpen: uiState.isReportModalOpen,
        setIsReportModalOpen: uiState.setIsReportModalOpen,
        reportSummaryRefreshToken: uiState.reportSummaryRefreshToken,
        isAiModalOpen: uiState.isAiModalOpen,
        setIsAiModalOpen: uiState.setIsAiModalOpen,
        isAnswerModalOpen: uiState.isAnswerModalOpen,
        setIsAnswerModalOpen: uiState.setIsAnswerModalOpen,
        isAiAnalysisLoading: progress.isAiAnalysisLoading,
        isAiAnalysisDisabled:
            activeTable === 'reviews'
                ? viewModel.isAiAnalysisForReviewsDisabled
                : viewModel.isAiAnalysisForGroupDisabled,
        aiAnalysisDisabledReason: viewModel.aiAnalysisDisabledReason,
        isAiAnalysisQuotaExhausted: viewModel.isAiAnalysisQuotaExhausted,
        onRunAiAnalysis:
            activeTable === 'reviews'
                ? actionHandlers.onRunAiAnalysisForSelectedReviews
                : actionHandlers.onRunAiAnalysisForSelectedProducts,
        pickedReviewsAnswer: uiState.pickedReviewsAnswer,
        setPickedReviewsAnswer: uiState.setPickedReviewsAnswer,
        isCombinedAnalysisModalOpen: combinedAnalysis.isCombinedAnalysisModalOpen,
        closeCombinedAnalysis: combinedAnalysis.closeCombinedAnalysis,
        openedCombinedAnalysis: combinedAnalysis.openedCombinedAnalysis,
        combinedAnalysisError: combinedAnalysis.combinedAnalysisError,
        isCombinedAnalysisLoading: combinedAnalysis.isCombinedAnalysisLoading,
        pickedBrandGroupsCount: pickedCounts.pickedBrandGroupsCount,
        pickedProductsCount: pickedCounts.pickedProductsCount,
        isGroupMergeModalOpen: groupMerge.isGroupMergeModalOpen,
        closeGroupMergeModal: groupMerge.closeGroupMergeModal,
        groupMergeNameDraft: groupMerge.groupMergeNameDraft,
        setGroupMergeNameDraft: groupMerge.setGroupMergeNameDraft,
        submitGroupMergeForm: groupMerge.submitGroupMergeForm,
        isGroupMergeSubmitDisabled: groupMerge.isGroupMergeSubmitDisabled,
        groupMergeEntityLabel: groupMerge.groupMergeEntityLabel,
        isGroupsMergeLoading: uiState.isGroupsMergeLoading,
    };

    return {
        buttonRowBindings,
        modalsBindings,
    };
};
